/**
 * Non-color scales — spacing, radius, typography, and elevation.
 *
 * Keys use Mantine's t-shirt sizing (`xs`…`xl`) so the semantic→Mantine adapter is a
 * one-to-one passthrough, but the values are framework-agnostic CSS strings.
 * `radius.md` is the brand default (`theme.defaultRadius === 'md'`); typography
 * is anchored on "Space Grotesk" with 600-weight headings, both reused from
 * `theme.ts`.
 */
/** Spacing scale (rem-based), used for padding, margins, and gaps. */
export const spacing = {
    xs: '0.625rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '2rem',
};
/** Corner-radius scale. `md` is the brand default radius. */
export const radius = {
    xs: '0.125rem',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    xl: '2rem',
    full: '9999px',
};
/** Font family stacks. `body` and `heading` share the brand "Space Grotesk". */
export const fontFamily = {
    body: '"Space Grotesk", system-ui, -apple-system, sans-serif',
    heading: '"Space Grotesk", system-ui, -apple-system, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
};
/** Font-size scale (rem-based). */
export const fontSize = {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
};
/** Line-height scale (unitless multipliers). */
export const lineHeight = {
    xs: '1.4',
    sm: '1.45',
    md: '1.55',
    lg: '1.6',
    xl: '1.65',
};
/** Font-weight scale. Headings use `semibold` (600), per the brand theme. */
export const fontWeight = {
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
};
/**
 * Fluid heading scale, expressed as CSS `clamp()` strings.
 *
 * Headings are the only type that moves between a phone and a desktop. The
 * t-shirt `fontSize` scale above deliberately does NOT move: `fontSize.md` is
 * what Mantine inputs inherit, and any input under 16px makes iOS Safari zoom
 * the page on focus. It is also what the PanelHeader (`sm`) and MetricBadge
 * (`xs`) rulings read, so stepping it would silently re-rule them. Keeping the
 * responsive scale in its own group is what protects all three.
 *
 * Each maximum equals the Mantine 8 default for that level, so nothing above
 * roughly 1000px changes; the minimum lands at a 390px viewport. Both ends are
 * pinned by tests that resolve the clamp at 390 and 1440 rather than by a
 * second copy of the numbers in the token.
 *
 * The middle term keeps a `rem` component on purpose: a pure-`vw` fluid size
 * stops responding to browser zoom, which fails WCAG 1.4.4.
 *
 * Line heights equal the Mantine 8 defaults exactly, and are carried here only
 * so this group is the whole answer for a heading level rather than half of it.
 * Tightening them — the responsive audit proposed 1.25/1.3/1.35 for h1-h3 — is a
 * desktop change on every heading in the app, because `mergeMantineTheme` deep
 * merges and a line height has no viewport term to confine it to phones. It is
 * a real improvement for two-line phone titles and wants its own decision.
 *
 * h5 and h6 are already at or below the body size, so they are fixed — there is
 * nothing to shrink without crossing the 16px input floor.
 */
export const headingSize = {
    h1: { fontSize: 'clamp(1.5rem, 1.1rem + 1.6vw, 2.125rem)', lineHeight: '1.3' },
    h2: { fontSize: 'clamp(1.25rem, 1rem + 1.1vw, 1.625rem)', lineHeight: '1.35' },
    h3: { fontSize: 'clamp(1.125rem, 0.95rem + 0.8vw, 1.375rem)', lineHeight: '1.4' },
    h4: { fontSize: 'clamp(1rem, 0.9rem + 0.5vw, 1.125rem)', lineHeight: '1.45' },
    h5: { fontSize: '1rem', lineHeight: '1.5' },
    h6: { fontSize: '0.875rem', lineHeight: '1.5' },
};
/** Grouped typography namespace mirroring the `typography.*` token paths. */
export const typography = {
    fontFamily,
    fontSize,
    lineHeight,
    fontWeight,
    headingSize,
};
/**
 * Elevation / box-shadow scale, lowest → highest.
 *
 * Three steps, not five. The card census found `shadow="sm"` (7 sites) and
 * `shadow="md"` (~12) used interchangeably for "slightly raised" — two authors
 * reaching for the same idea, not two meanings — so they collapse into one
 * `raised` step. `flat` is a real `none` because a card inside a page has not
 * left the page, and flat is the resting state for the large majority of
 * surfaces. `overlay` is reserved for modals: a card that looks like it left the
 * page is a card pretending to be a dialog.
 *
 * Shadows are tinted with the darkest brand purple — `purple[9]` `#1a001a` =
 * `rgba(26,0,26,α)` — never neutral black. On a purple-led brand a grey shadow
 * under a white card reads slightly dirty. Note this is deliberately NOT
 * `commonColors.ink`, which is `purple[7]`: ink is the foreground token, too
 * light to sit under a surface as a shadow. The `overlay` value is the one
 * already shipping on ActionDialog, so it is proven rather than proposed.
 *
 * This replaces the previous five-step black-shadow scale, which had no
 * consumers — it was deliberately left unwired from
 * `theme.shadows` so Mantine's defaults kept rendering. Nothing in the app reads
 * it, so the replacement changes no pixels until a component opts in.
 */
export const elevation = {
    flat: 'none',
    raised: '0 1px 2px rgba(26,0,26,0.06), 0 2px 8px rgba(26,0,26,0.08)',
    overlay: '0 8px 24px rgba(26,0,26,0.14)',
    /** Cast sideways by a sticky column over the content scrolled under it. */
    stickyEdge: '6px 0 6px -4px rgba(26,0,26,0.15)',
};
/**
 * Stacking order for elements that overlap in place, within one surface. It
 * starts low on purpose: overlays (popovers, menus, tooltips, dialogs) render in
 * a portal and stack far above, so nothing here competes with them.
 */
export const layer = {
    /** A sticky table column, over the cells that scroll under it. */
    stickyColumn: '1',
};
